import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal as GoalModel, Segment } from '@feathr/blackbox';
import { Alert } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import ConversionPixelSelect from './ConversionPixelSelect';

import * as styles from './ConversionPixelTriggerGoal.css';

interface IProps {
  goal: GoalModel;
}

function ConversionPixelTriggerGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const [selectedPixel, setSelectedPixel] = useState<Segment | undefined>();
  const {
    spacing: { '4': gap },
  } = useMantineTheme();

  useEffect(() => {
    const segmentId = goal.get('segment');
    if (!segmentId) {
      return;
    }
    const segment = Segments.get(segmentId);
    setSelectedPixel(segment);
  }, []);

  function handlePixelChange(segment: Segment | undefined): void {
    if (segment) {
      goal.set({ segment: segment.id });
      setSelectedPixel(segment);
    } else {
      goal.set({ segment: undefined });
      setSelectedPixel(undefined);
    }
  }

  return (
    <Flex direction={'column'} gap={gap}>
      <Alert
        className={styles.alert}
        description={t(
          'Conversion values will be automatically captured from the pixel when it fires. No manual value assignment is available.',
        )}
        title={t('Important')}
        type={'info'}
      />
      <ConversionPixelSelect onChange={handlePixelChange} value={selectedPixel?.id} />
    </Flex>
  );
}

export default observer(ConversionPixelTriggerGoal);
